import { useEffect, useState } from 'react';
import axios from 'axios';

const API = '/api';

type Tenant = { id: number; name: string };
type Trunk  = { id: number; trunk_name: string; display_name: string };
type Route  = {
  id: number;
  tenant_id: number;
  route_name: string;
  pattern: string;
  trunk_id: number;
  trunk_name: string;
  trunk_display: string;
  priority: number;
  strip_digits: number;
  prepend: string;
  is_active: number;
};

const S = {
  label: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: 4 },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.3)',
    color: '#f1f5f9', outline: 'none', boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
    background: '#1e293b', border: '1px solid rgba(100,116,139,0.3)',
    color: '#f1f5f9', outline: 'none', boxSizing: 'border-box' as const,
  },
};

const EMPTY: Partial<Route> = { route_name: '', pattern: '', trunk_id: 0, priority: 10, strip_digits: 0, prepend: '', is_active: 1 };

export default function OutboundRoutesPage({ showTenantSelector }: { showTenantSelector?: boolean }) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [trunks, setTrunks] = useState<Trunk[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<Route>>(EMPTY);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(`${API}/asterisk/tenants`).then(r => {
      const list: Tenant[] = r.data.tenants || r.data || [];
      setTenants(list);
      if (list.length > 0) setTenantId(list[0].id);
    }).catch(() => {});
    axios.get(`${API}/trunks`).then(r => setTrunks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    axios.get(`${API}/outbound-routes?tenant_id=${tenantId}`)
      .then(r => setRoutes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRoutes([]));
  }, [tenantId]);

  function flash(ok: string) { setMsg(ok); setErr(''); setTimeout(() => setMsg(''), 3500); }
  function flashErr(e: string) { setErr(e); setMsg(''); setTimeout(() => setErr(''), 4500); }

  async function save() {
    if (!form.route_name?.trim() || !form.pattern?.trim() || !form.trunk_id || !tenantId) {
      flashErr('Route name, pattern and trunk are required');
      return;
    }
    setSaving(true);
    try {
      if (editId !== null) {
        await axios.put(`${API}/outbound-routes/${editId}`, form);
        flash('Route updated — dialplan resynced');
      } else {
        await axios.post(`${API}/outbound-routes`, { ...form, tenant_id: tenantId });
        flash('Route added — dialplan resynced');
      }
      setForm(EMPTY);
      setEditId(null);
      const r = await axios.get(`${API}/outbound-routes?tenant_id=${tenantId}`);
      setRoutes(Array.isArray(r.data) ? r.data : []);
    } catch (e: any) {
      flashErr(e?.response?.data?.error || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function del(id: number) {
    if (!window.confirm('Delete this route? Routing engine will be resynced immediately.')) return;
    try {
      await axios.delete(`${API}/outbound-routes/${id}`);
      flash('Route deleted — dialplan resynced');
      setRoutes(r => r.filter(x => x.id !== id));
      if (editId === id) { setForm(EMPTY); setEditId(null); }
    } catch (e: any) {
      flashErr(e?.response?.data?.error || e?.message || 'Delete failed');
    }
  }

  function startEdit(r: Route) {
    setEditId(r.id);
    setForm({ route_name: r.route_name, pattern: r.pattern, trunk_id: r.trunk_id, priority: r.priority, strip_digits: r.strip_digits, prepend: r.prepend, is_active: r.is_active });
  }

  const selectedTenant = tenants.find(t => t.id === tenantId);

  return (
    <div style={{ padding: '28px 24px', maxWidth: 980, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', marginBottom: 4 }}>Outbound Routes</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          Per-destination routing rules. Specific patterns take priority over trunk catch-alls. Changes resync the routing engine immediately.
        </div>
      </div>

      {/* Tenant selector */}
      {showTenantSelector !== false && tenants.length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <div style={S.label}>Tenant</div>
          <select style={{ ...S.select, maxWidth: 280 }} value={tenantId ?? ''} onChange={e => setTenantId(Number(e.target.value))}>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}

      {/* Form */}
      <div style={{ background: 'rgba(30,41,59,0.8)', borderRadius: 14, border: '1px solid rgba(139,92,246,0.2)', padding: '20px 22px', marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', marginBottom: 16 }}>
          {editId !== null ? '✏️ Edit Route' : '➕ Add Route'}{selectedTenant ? ` — ${selectedTenant.name}` : ''}
        </div>

        {msg && <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(16,185,129,0.12)', borderRadius: 6, border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', fontSize: 13 }}>{msg}</div>}
        {err && <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.12)', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={S.label}>Route Name</div>
            <input style={S.input} placeholder="e.g. UAE Dubai Landline" value={form.route_name || ''} onChange={e => setForm(f => ({ ...f, route_name: e.target.value }))} />
          </div>
          <div>
            <div style={S.label}>Dial Pattern</div>
            <input style={S.input} placeholder="e.g. _04XXXXXXX or _+." value={form.pattern || ''} onChange={e => setForm(f => ({ ...f, pattern: e.target.value }))} />
          </div>
          <div>
            <div style={S.label}>Outbound Trunk</div>
            <select style={S.select} value={form.trunk_id || ''} onChange={e => setForm(f => ({ ...f, trunk_id: Number(e.target.value) }))}>
              <option value="">— Select trunk —</option>
              {trunks.map(t => <option key={t.id} value={t.id}>{t.display_name || t.trunk_name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '120px 120px 1fr 80px', gap: 14, marginBottom: 18 }}>
          <div>
            <div style={S.label}>Priority</div>
            <input type="number" style={S.input} value={form.priority ?? 10} onChange={e => setForm(f => ({ ...f, priority: Number(e.target.value) }))} />
          </div>
          <div>
            <div style={S.label}>Strip Digits</div>
            <input type="number" style={S.input} value={form.strip_digits ?? 0} onChange={e => setForm(f => ({ ...f, strip_digits: Number(e.target.value) }))} />
          </div>
          <div>
            <div style={S.label}>Prepend</div>
            <input style={S.input} placeholder="optional prefix to add" value={form.prepend || ''} onChange={e => setForm(f => ({ ...f, prepend: e.target.value }))} />
          </div>
          <div>
            <div style={S.label}>Active</div>
            <select style={S.select} value={form.is_active ?? 1} onChange={e => setForm(f => ({ ...f, is_active: Number(e.target.value) }))}>
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={save}
            disabled={saving}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: saving ? '#334155' : 'linear-gradient(135deg,#8b5cf6,#6d28d9)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: saving ? 'default' : 'pointer' }}
          >
            {saving ? 'Saving…' : (editId !== null ? 'Update Route' : 'Add Route')}
          </button>
          {editId !== null && (
            <button
              onClick={() => { setForm(EMPTY); setEditId(null); }}
              style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid rgba(100,116,139,0.3)', background: 'transparent', color: '#94a3b8', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Route table */}
      <div style={{ background: 'rgba(30,41,59,0.6)', borderRadius: 14, border: '1px solid rgba(139,92,246,0.12)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(139,92,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#e2e8f0' }}>
            Routes — {selectedTenant?.name || '…'} ({routes.length})
          </span>
          <span style={{ fontSize: 12, color: '#64748b' }}>sorted by priority → id</span>
        </div>
        {routes.length === 0 ? (
          <div style={{ padding: '36px 18px', textAlign: 'center', color: '#475569', fontSize: 13 }}>
            No outbound routes for this tenant. Add one above.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(15,23,42,0.5)' }}>
                {['Pri', 'Route Name', 'Pattern', 'Trunk', 'Strip', 'Prepend', 'Active', ''].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {routes.map((r, i) => (
                <tr
                  key={r.id}
                  style={{ background: editId === r.id ? 'rgba(139,92,246,0.1)' : (i % 2 === 0 ? 'transparent' : 'rgba(15,23,42,0.3)'), borderBottom: '1px solid rgba(139,92,246,0.06)' }}
                >
                  <td style={{ padding: '9px 14px', fontSize: 13, color: '#94a3b8', fontWeight: 700 }}>{r.priority}</td>
                  <td style={{ padding: '9px 14px', fontSize: 13, color: '#f1f5f9' }}>{r.route_name}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <code style={{ background: 'rgba(139,92,246,0.1)', color: '#c4b5fd', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{r.pattern}</code>
                  </td>
                  <td style={{ padding: '9px 14px', fontSize: 13, color: '#94a3b8' }}>{r.trunk_display || r.trunk_name || `ID:${r.trunk_id}`}</td>
                  <td style={{ padding: '9px 14px', fontSize: 13, color: '#64748b' }}>{r.strip_digits || 0}</td>
                  <td style={{ padding: '9px 14px', fontSize: 13, color: '#64748b' }}>{r.prepend || '—'}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: r.is_active ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: r.is_active ? '#6ee7b7' : '#fca5a5' }}>
                      {r.is_active ? 'Active' : 'Off'}
                    </span>
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => startEdit(r)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(139,92,246,0.4)', background: 'transparent', color: '#a78bfa', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                      <button onClick={() => del(r.id)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
